/**
 * HTTP Client Utility
 *
 * Provides a unified HTTP client for all API requests with:
 * - Consistent error handling
 * - Automatic API key headers
 * - Request timeouts via AbortController
 * - Secure logging (redacted secrets)
 */

import { NetworkError, SDKErrorCode } from '../errors';
import { redactSecrets } from '../utils/log-redaction';

// ============================================================
// Types
// ============================================================

export interface HttpClientConfig {
  /** Base URL for all requests */
  baseUrl: string;

  /** API key for authentication (sent in X-API-Key header) */
  apiKey?: string;

  /** Default timeout in milliseconds (default: 30000) */
  timeout?: number;

  /** Include credentials in requests (default: false) */
  includeCredentials?: boolean;

  /** Enable debug logging (default: false) */
  debug?: boolean;

  /** Custom logger function */
  logger?: (...args: unknown[]) => void;
}

export interface RequestOptions {
  /** HTTP method (default: 'GET') */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

  /** Request body (will be JSON stringified for objects) */
  body?: unknown;

  /** Additional headers */
  headers?: Record<string, string>;

  /** Override default timeout */
  timeout?: number;

  /** Skip API key header */
  skipApiKey?: boolean;

  /** Custom content type (default: application/json for POST/PUT/PATCH) */
  contentType?: string;
}

export interface HttpResponse<T> {
  /** Response data */
  data: T;

  /** HTTP status code */
  status: number;

  /** Response headers */
  headers: Headers;

  /** Whether response was successful (2xx) */
  ok: boolean;
}

// ============================================================
// HttpClient Class
// ============================================================

/**
 * HTTP client for making API requests with consistent handling
 */
export class HttpClient {
  private readonly config: Required<HttpClientConfig>;

  constructor(config: HttpClientConfig) {
    this.config = {
      baseUrl: config.baseUrl,
      apiKey: config.apiKey || '',
      timeout: config.timeout ?? 30000,
      includeCredentials: config.includeCredentials ?? false,
      debug: config.debug ?? false,
      logger: config.logger ?? console.log,
    };
  }

  /**
   * Make a GET request
   */
  async get<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  async post<T, B = unknown>(
    endpoint: string,
    body?: B,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * Make a PUT request
   */
  async put<T, B = unknown>(
    endpoint: string,
    body?: B,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(
    endpoint: string,
    options?: Omit<RequestOptions, 'method'>
  ): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Make a PATCH request
   */
  async patch<T, B = unknown>(
    endpoint: string,
    body?: B,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * Upload a file using FormData
   */
  async upload<T>(
    endpoint: string,
    formData: FormData,
    options?: Omit<RequestOptions, 'method' | 'body' | 'contentType'>
  ): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
      contentType: '', // Let browser set multipart/form-data with boundary
    });
  }

  /**
   * Core request method
   */
  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<HttpResponse<T>> {
    const {
      method = 'GET',
      body,
      headers = {},
      timeout = this.config.timeout,
      skipApiKey = false,
      contentType,
    } = options;

    const url = this.buildUrl(endpoint);

    // Build headers
    const requestHeaders: Record<string, string> = { ...headers };

    // Add API key if configured and not skipped
    if (this.config.apiKey && !skipApiKey) {
      requestHeaders['X-API-Key'] = this.config.apiKey;
    }

    // Add content type for requests with body
    if (body !== undefined && !(body instanceof FormData)) {
      requestHeaders['Content-Type'] = contentType ?? 'application/json';
    }

    // Prepare body
    let requestBody: string | FormData | undefined;
    if (body !== undefined) {
      if (body instanceof FormData) {
        requestBody = body;
      } else if (typeof body === 'object') {
        requestBody = JSON.stringify(body);
      } else {
        requestBody = String(body);
      }
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    this.log('Request:', {
      method,
      url,
      headers: redactSecrets(requestHeaders),
    });

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: requestBody,
        signal: controller.signal,
        credentials: this.config.includeCredentials ? 'include' : 'omit',
      });

      clearTimeout(timeoutId);

      // Handle response
      return this.handleResponse<T>(response, endpoint);
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error, endpoint);
    }
  }

  /**
   * Handle successful response
   */
  private async handleResponse<T>(response: Response, endpoint: string): Promise<HttpResponse<T>> {
    // Try to parse JSON response
    let data: T;
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      try {
        data = await response.json();
      } catch {
        // If JSON parsing fails, treat as empty object
        data = {} as T;
      }
    } else {
      // For non-JSON responses, return text as data
      const text = await response.text();
      data = text as unknown as T;
    }

    this.log('Response:', {
      status: response.status,
      ok: response.ok,
      endpoint,
    });

    // Check for error status codes
    if (!response.ok) {
      throw this.createHttpError(response.status, data, endpoint);
    }

    return {
      data,
      status: response.status,
      headers: response.headers,
      ok: response.ok,
    };
  }

  /**
   * Handle fetch errors
   */
  private handleError(error: unknown, endpoint: string): NetworkError {
    if (error instanceof NetworkError) {
      return error;
    }

    if (error instanceof Error) {
      // Handle abort (timeout)
      if (error.name === 'AbortError') {
        return NetworkError.timeout(endpoint);
      }

      // Handle network errors
      if (error.message.includes('fetch') || error.message.includes('network')) {
        return new NetworkError(SDKErrorCode.NETWORK_ERROR, error.message, {
          context: endpoint,
          cause: error,
        });
      }

      // Check if offline
      if (!navigator.onLine) {
        return NetworkError.offline();
      }

      return new NetworkError(SDKErrorCode.NETWORK_ERROR, error.message, {
        context: endpoint,
        cause: error,
      });
    }

    return new NetworkError(SDKErrorCode.NETWORK_ERROR, String(error), { context: endpoint });
  }

  /**
   * Create HTTP error from response
   */
  private createHttpError<T>(status: number, data: T, endpoint: string): NetworkError {
    // Extract error message from response
    let message = `HTTP ${status}`;

    if (data && typeof data === 'object') {
      const errorData = data as Record<string, unknown>;
      message = (errorData.detail || errorData.message || errorData.error || message) as string;
    }

    return NetworkError.apiError(status, message, endpoint);
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    // If endpoint is already a full URL, use it directly
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }

    // Ensure proper URL construction
    const base = this.config.baseUrl.replace(/\/$/, '');
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    return `${base}${path}`;
  }

  /**
   * Log with debug check and redaction
   */
  private log(...args: unknown[]): void {
    if (this.config.debug) {
      const safeArgs = args.map((arg) =>
        typeof arg === 'object' && arg !== null
          ? redactSecrets(arg as Record<string, unknown>)
          : arg
      );
      this.config.logger('[HttpClient]', ...safeArgs);
    }
  }

  /**
   * Update API key
   */
  setApiKey(apiKey: string): void {
    this.config.apiKey = apiKey;
  }

  /**
   * Get current base URL
   */
  getBaseUrl(): string {
    return this.config.baseUrl;
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create a new HttpClient instance
 */
export function createHttpClient(config: HttpClientConfig): HttpClient {
  return new HttpClient(config);
}

// ============================================================
// Utility Types
// ============================================================

/**
 * Standard API response envelope
 */
export interface ApiResponse<T> {
  code: number;
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Standard API error response
 */
export interface ApiErrorResponse {
  detail?: string;
  message?: string;
  error?: string;
  code?: number;
}

/**
 * Type guard for API response
 */
export function isApiResponse<T>(response: unknown): response is ApiResponse<T> {
  return (
    typeof response === 'object' && response !== null && 'success' in response && 'data' in response
  );
}
